import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, token } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (token) return <Navigate to="/dashboard" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex bg-[--paper]">
      {/* Left editorial panel */}
      <div
        className="hidden md:flex w-[52%] relative overflow-hidden text-white grain"
        style={{
          background:
            "radial-gradient(1200px 600px at 20% 20%, rgba(59,130,246,0.28), transparent 50%), radial-gradient(1000px 500px at 80% 80%, rgba(30,64,175,0.28), transparent 50%), #05070d",
        }}
      >
        {/* Grid pattern */}
        <svg
          className="absolute inset-0 w-full h-full opacity-[0.06]"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <pattern id="grid" width="48" height="48" patternUnits="userSpaceOnUse">
              <path d="M 48 0 L 0 0 0 48" fill="none" stroke="#fff" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>

        <div className="relative z-10 flex flex-col justify-between p-14 w-full">
          <div className="flex items-center gap-3">
            <div
              className="w-14 h-14 rounded-lg flex items-center justify-center shrink-0"
              style={{
                background:
                  "radial-gradient(circle at 30% 30%, rgba(59,130,246,0.3), rgba(30,64,175,0.15) 60%, transparent 100%)",
                border: "1px solid rgba(255,255,255,0.1)",
              }}
            >
              <img
                src="/logo.png"
                alt="Afridi & Co."
                className="w-12 h-12 object-contain"
                style={{ filter: "drop-shadow(0 4px 12px rgba(30,64,175,0.5))" }}
              />
            </div>
            <div className="leading-tight">
              <div className="font-serif text-xl text-white leading-none">
                Afridi<span className="text-blue-400">&amp;</span>Co.
              </div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mt-1">
                Est. Tarnol, Islamabad
              </div>
            </div>
          </div>

          <div className="max-w-lg">
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-white/15 rounded-full text-[10px] uppercase tracking-[0.28em] text-slate-300 mb-8">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Point of Sale · Ledger · Inventory
            </div>
            <h1 className="font-serif text-[64px] leading-[0.95] text-white tracking-tight">
              A quieter tool for a{" "}
              <span className="italic text-blue-300">heavy-duty</span> business.
            </h1>
            <p className="mt-6 text-slate-400 text-base max-w-md leading-relaxed">
              Sell parts, print invoices, and reconcile ledgers — designed for the pace of a real
              workshop, not a demo. Deals in all kinds of heavy machinery auto parts.
            </p>

            <div className="mt-10 flex items-center gap-8 text-xs text-slate-400">
              <div>
                <div className="font-serif text-2xl text-white">A4</div>
                <div className="uppercase tracking-widest text-[9px] mt-1">Invoice format</div>
              </div>
              <div className="h-8 w-px bg-white/10" />
              <div>
                <div className="font-serif text-2xl text-white">PKR ₨</div>
                <div className="uppercase tracking-widest text-[9px] mt-1">Currency</div>
              </div>
              <div className="h-8 w-px bg-white/10" />
              <div>
                <div className="font-serif text-2xl text-white">A&amp;Co</div>
                <div className="uppercase tracking-widest text-[9px] mt-1">Since day one</div>
              </div>
            </div>
          </div>

          <div className="text-[10px] uppercase tracking-[0.28em] text-slate-600">
            © {new Date().getFullYear()} Afridi&amp;Co. · All rights reserved.
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-[380px]">
          <div className="md:hidden flex items-center gap-2 mb-10 justify-center">
            <img src="/logo.png" alt="Afridi & Co." className="w-10 h-10 object-contain" />
            <div className="font-serif text-xl">
              Afridi<span className="text-blue-700">&amp;</span>Co.
            </div>
          </div>

          <div className="mb-10">
            <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-4">
              Private access
            </div>
            <h2 className="font-serif text-4xl text-slate-900 mb-2 leading-tight">Sign in</h2>
            <p className="text-sm text-slate-500">
              Enter your credentials to open the shop console.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label
                htmlFor="email"
                className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-medium"
              >
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                placeholder="you@example.com"
                autoComplete="email"
                required
                className="mt-2 h-11 border-slate-200 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0"
              />
            </div>
            <div>
              <Label
                htmlFor="password"
                className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-medium"
              >
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                placeholder="••••••••"
                autoComplete="current-password"
                required
                className="mt-2 h-11 border-slate-200 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white rounded-md font-medium tracking-tight"
            >
              {loading ? (
                <Loader2 className="animate-spin h-4 w-4" />
              ) : (
                <>
                  <span>Sign in</span>
                  <ArrowRight size={16} className="ml-2" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-10 pt-6 border-t border-slate-100 text-[11px] text-slate-400">
            <div className="mb-2">
              This is a private console for staff of Afridi&amp;Co. only.
            </div>
            <div className="text-slate-500">
              <span className="uppercase tracking-[0.2em] text-[9.5px] text-slate-400">Accounts</span>
              <div className="mt-1 font-mono-tabular text-[10.5px] leading-relaxed">
                admin@afridi.com  ·  full access<br />
                shop@afridi.com   ·  no reports
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
